import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGamesBase } from '../../utils/gamesBase';
import { 
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const PPTGameResults = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const gamesBase = useGamesBase();
  const location = useLocation();
  
  const gameData = location.state || {};
  
  if (!gameData.final_score) {
    navigate(`${gamesBase}/ppt/${subjectId}`);
    return null;
  }

  const correctCount = gameData.correct_count || 0;
  const totalTasks = gameData.total_tasks || 20;
  const incorrectCount = totalTasks - correctCount;
  const finalScore = gameData.final_score || 0;
  const timeSpent = gameData.time_spent_seconds || 0;
  const tasksData = gameData.tasks_data || [];

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-500';
    if (score >= 75) return 'text-green-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreMessage = (score) => {
    if (score >= 90) return 'Excellent! Outstanding performance!';
    if (score >= 75) return 'Great job! Well done!';
    if (score >= 60) return 'Good effort! Keep practicing!';
    return 'Keep practicing to improve!';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Game Complete!
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {getScoreMessage(finalScore)}
            </p>
          </div>

          {/* Score Display */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <svg className="transform -rotate-90" width="200" height="200">
                <circle
                  cx="100"
                  cy="100"
                  r="90"
                  stroke="#e5e7eb"
                  strokeWidth="12"
                  fill="none"
                />
                <circle
                  cx="100"
                  cy="100"
                  r="90"
                  stroke="#10b981"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${(finalScore / 100) * 565.48} 565.48`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className={`text-5xl font-bold ${getScoreColor(finalScore)}`}>
                    {finalScore.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Final Score
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 text-center">
              <CheckCircleIcon className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {correctCount}
              </div>
              <div className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
                Correct
              </div>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-center">
              <XCircleIcon className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {incorrectCount}
              </div>
              <div className="text-xs text-red-600/70 dark:text-red-400/70">
                Incorrect
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {totalTasks}
              </div>
              <div className="text-xs text-blue-600/70 dark:text-blue-400/70">
                Total Tasks
              </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 text-center">
              <ClockIcon className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {formatTime(timeSpent)}
              </div>
              <div className="text-xs text-amber-600/70 dark:text-amber-400/70">
                Time Spent
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate(`${gamesBase}/ppt/${subjectId}`)}
              className="flex-1 py-3 px-6 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back to Setup</span>
            </button>
            
            <button
              onClick={() => window.location.reload()}
              className="flex-1 py-3 px-6 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg font-medium transition-all flex items-center justify-center space-x-2"
            >
              <ArrowPathIcon className="h-5 w-5" />
              <span>Play Again</span>
            </button>
          </div>
        </div>

        {/* Task-by-Task Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Task Breakdown
          </h2>
          
          <div className="space-y-2">
            {tasksData.map((task, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  task.isCorrect
                    ? 'bg-emerald-50 dark:bg-emerald-900/20'
                    : 'bg-red-50 dark:bg-red-900/20'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {task.isCorrect ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-500" />
                  )}
                  <span className="font-medium text-gray-900 dark:text-white">
                    Task {task.taskNumber}
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 text-sm">
                  <div className="text-gray-600 dark:text-gray-400">
                    Your answer: <span className="font-medium">{task.userAnswer}</span>
                  </div>
                  {!task.isCorrect && (
                    <div className="text-gray-600 dark:text-gray-400">
                      Correct: <span className="font-medium text-emerald-600 dark:text-emerald-400">
                        {task.correctAnswer}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PPTGameResults;
